import React, { useState } from 'react';
import { useTheme } from '../components/ThemeContext';
import { StockSearch } from '../components/StockSearch';
import ETFDetail from './ETFDetail';
import { SEOMeta, getToolSEOMeta } from '../components/SEOMeta';

interface Etf {
  symbol: string;
  name: string;
  price: number;
  change: number;
  changePercent: number;
}

const ETFs: React.FC = () => {
  const { theme } = useTheme();
  const [selectedEtf, setSelectedEtf] = useState<Etf | null>(null);
  const [holdings, setHoldings] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleSelect = async (symbol: string) => {
    setLoading(true);
    setHoldings([]);
    try {
      // Hardcode quarters as requested
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentQtr = 2;
      const previousYear = currentYear;
      const previousQtr = 1;
      const url = `/api/funds/compare-holdings?symbol=${symbol}&currentYear=${currentYear}&currentQtr=${currentQtr}&previousYear=${previousYear}&previousQtr=${previousQtr}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch ETF holdings');
      const data = await response.json();
      setHoldings(data);
      // Use first holding for summary info
      if (data.length > 0) {
        setSelectedEtf({
          symbol: symbol,
          name: data[0].fundName || symbol + ' ETF',
          price: data[0].fundPrice || 0,
          change: data[0].fundChange || 0,
          changePercent: data[0].fundChangePercent || 0,
        });
      } else {
        setSelectedEtf({ symbol, name: symbol + ' ETF', price: 0, change: 0, changePercent: 0 });
      }
    } catch (error) {
      setSelectedEtf({ symbol, name: symbol + ' ETF', price: 0, change: 0, changePercent: 0 });
      setHoldings([]);
    } finally {
      setLoading(false);
    }
  };

  const seoMeta = getToolSEOMeta('etfs');

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-theme-primary text-gray-200' : 'bg-gray-100 text-gray-800'}`}>
      <SEOMeta
        title={seoMeta.title}
        description={seoMeta.description}
        keywords={seoMeta.keywords}
      />
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6">
          <StockSearch
            placeholder="Search ETFs..."
            onSelect={handleSelect}
          />
        </div>

        {loading && (
          <div className={`p-8 rounded-lg text-center ${theme === 'dark' ? 'bg-theme-secondary' : 'bg-white'}`}>
            <p>Loading ETF data...</p>
          </div>
        )}

        {!loading && <ETFDetail selectedEtf={selectedEtf} holdings={holdings} />}
      </div>
    </div>
  );
};

export default ETFs;
